import React, { useState } from "react"

import Booking from "~/types/Booking"
import { useToast } from "~/utils/Toast"
import getAppConfig from "~/utils/getAppConfig"
import { apiBookings } from "~/routes"
import CatchModal from "~/library/CatchModal"

interface BookingStatusManagerProps {
  booking: Booking
  onUpdate: () => void
}

interface StatusConfig {
  label: string
  buttonClass: string
  newStatus: string
}

const STATUS_CONFIG: { [key: string]: StatusConfig } = {
  pending: { label: "Confirm Booking", buttonClass: "btn-success", newStatus: "confirmed" },
}

const BookingStatusManager = ({ booking, onUpdate }: BookingStatusManagerProps) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const { venueSlug } = getAppConfig()
  const toast = useToast()

  const handleStatusChange = (newStatus: string) => {
    if (isUpdating) return
    setIsUpdating(true)

    const data = {
      venue_slug: venueSlug,
      id: booking.id,
      booking: {
        status: newStatus,
      },
    }

    apiBookings
      .update({ data })
      .then(() => {
        toast("Booking status updated.", "success")
        onUpdate()
      })
      .catch((res: any) => {
        toast(res.body.errors.join(", "), "danger")
      })
      .finally(() => {
        setIsUpdating(false)
        setShowConfirmModal(false)
      })
  }

  const handleCancel = () => {
    setShowConfirmModal(false)
  }

  const currentStatusConfig = STATUS_CONFIG[booking.status]

  // Only show button for pending status
  if (!currentStatusConfig) return null

  const confirmationMessage = "Are you sure you want to confirm this booking?"

  return (
    <>
      <button
        className={`btn ${currentStatusConfig.buttonClass}`}
        onClick={() => setShowConfirmModal(true)}
        disabled={isUpdating}
      >
        {isUpdating ? "Updating..." : currentStatusConfig.label}
      </button>

      <CatchModal
        isOpen={showConfirmModal}
        onClose={handleCancel}
        title="Confirm Status Change"
        footer={
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={isUpdating}
            >
              No
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleStatusChange(currentStatusConfig.newStatus)}
              disabled={isUpdating}
            >
              {isUpdating ? "Updating..." : "Yes"}
            </button>
          </div>
        }
      >
        <p>{confirmationMessage}</p>
      </CatchModal>
    </>
  )
}

export default BookingStatusManager
